import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { Divider, Grid, Switch, Typography, Button, IconButton } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import classNames from 'classnames';

import { S3Object, useAdminUpdateClientMutation, useDeleteClientStickerMutation } from 'api';
import { useClient, useNotify } from 'hooks';
import { getImageSrc } from 'helpers';
import { Alert } from 'components';
import { adminUpdateClientResponse, deleteClientStickerResponse } from 'apollo/responses';
import { queryString } from 'utilities';

import Tip from 'components/Tip';
import StickerUpload from './StickerUpload';
import { STICKER_SIZE } from './constants';

import { IMGIX_HOST } from 'settings';

const useStyles = makeStyles(theme => ({
  divider: {
    margin: theme.spacing(3, 0),
  },
  subtitle: {
    marginBottom: theme.spacing(1.5),
  },
  removeStickerIcon: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    opacity: '.7',
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  checkeredBackground: theme.mixins.checkeredBackground,
  stickerItem: {
    width: `${STICKER_SIZE}px`,
    height: `${STICKER_SIZE}px`,
    position: 'relative',
    border: `solid 1px ${theme.palette.grey[300]}`,
  },
  addSticker: {
    backgroundColor: theme.palette.grey[100],
  },
}));

enum NotifyType {
  Delete = 'delete',
  Toggle = 'toggle',
}

const CustomStickers: FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [showDelete, setShowDelete] = useState(false);
  const [currentSticker, setCurrentSticker] = useState(null);
  const client = useClient();
  const { id, stickers, stickers_enabled } = client;
  const notify = useNotify();

  const count = stickers.length;

  const [adminUpdateClientMutation] = useAdminUpdateClientMutation({
    onError: notify.mutationError,
    onCompleted: data => {
      const { adminUpdateClient } = data;
      const enabled = adminUpdateClient.stickers_enabled;
      notifySuccess(NotifyType.Toggle, enabled);
    },
  });

  const [deleteClientStickerMutation] = useDeleteClientStickerMutation({
    onError: () => {
      setShowDelete(false);
      setCurrentSticker(null);
      notify.mutationError();
    },
    onCompleted: () => notifySuccess(NotifyType.Delete),
  });

  function notifySuccess(type: NotifyType, enabled?: boolean) {
    let message = '';
    if (type === NotifyType.Delete) {
      message = t('components:deletedSuccessfully');
    }
    if (type === NotifyType.Toggle) {
      if (enabled) {
        message = t('components:enabledSuccessfully');
      } else {
        message = t('components:disabledSuccessfully');
      }
    }
    notify.info({ message });
  }

  function handleDeleteToggle(sticker: S3Object) {
    setShowDelete(!showDelete);
    setCurrentSticker(sticker);
  }

  function handleDeleteSticker() {
    const variables = {
      client_id: id,
      sticker_key: currentSticker.key,
    };

    return deleteClientStickerMutation({
      variables,
      optimisticResponse: deleteClientStickerResponse({ client, sticker: currentSticker }),
    });
  }

  function handleEnableStickerChange(event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    const data = { id, stickers_enabled: checked };

    return adminUpdateClientMutation({
      variables: { data },
      optimisticResponse: adminUpdateClientResponse({ ...client, ...data }),
    });
  }

  function renderStickers() {
    return stickers.map((sticker, idx) => {
      const params = {
        fit: 'fill',
        fill: 'solid',
        h: STICKER_SIZE,
        w: STICKER_SIZE,
        auto: 'format',
      };

      const imageUrl = `${IMGIX_HOST}/${sticker.key}?${queryString(params)}`;
      const imageSrc = getImageSrc(imageUrl);

      return (
        <Grid item key={idx}>
          <div className={classNames(classes.stickerItem, classes.checkeredBackground)}>
            <img {...imageSrc} />
            <IconButton
              className={classes.removeStickerIcon}
              onClick={() => handleDeleteToggle(sticker)}>
              <CancelIcon />
            </IconButton>
          </div>
        </Grid>
      );
    });
  }

  return (
    <Fragment>
      <Typography>{t('admin:shareStickersHelp')}</Typography>
      <Tip i18nKey="admin:shareStickersTip" />
      <Divider className={classes.divider} />
      <Grid container justify="space-between" className={classes.subtitle}>
        <Typography variant="subtitle1">{t('admin:shareStickersLibrary', { count })}</Typography>
        {!!stickers.length && (
          <Grid item>
            <Typography display="inline">{t('admin:shareStickersEnable')}</Typography>
            <Switch
              checked={stickers_enabled}
              onChange={handleEnableStickerChange}
              color="primary"
            />
          </Grid>
        )}
      </Grid>

      <Grid container alignItems="center" justify="flex-start" spacing={1}>
        <Grid item>
          <Button className={classNames(classes.stickerItem, classes.addSticker)}>
            <StickerUpload />
          </Button>
        </Grid>

        {renderStickers()}
      </Grid>

      <Alert
        open={showDelete}
        onConfirm={handleDeleteSticker}
        onCancel={() => handleDeleteToggle(null)}
        message={<Typography>{t('admin:shareStickersDeleteAlert')}</Typography>}
        confirm={<Typography>{t('common:delete')}</Typography>}
      />
    </Fragment>
  );
};

export default CustomStickers;
